import { existsSync, readFileSync } from "fs";
import { resolve } from "path";

type LoggerLike = Pick<Console, "log" | "warn">;

export const DEFAULT_PLATFORM_CONFIG_PATH = "/etc/gencow/platform-config.json";
const LOCAL_PLATFORM_CONFIG_FILE = "platform.config.json";

const ENV_KEY_PATTERN = /^[A-Z][A-Z0-9_]*$/;
const DENIED_ENV_KEYS = new Set([
  "DATABASE_URL",
  "BETTER_AUTH_SECRET",
  "BETTER_AUTH_URL",
  "PORT",
  "PATH",
  "HOME",
  "PWD",
  "NODE_PATH",
]);

type JsonObject = Record<string, unknown>;

function isObject(value: unknown): value is JsonObject {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function asScalarEnvValue(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  return undefined;
}

function setEnv(target: Record<string, string>, key: string, value: unknown): void {
  if (!ENV_KEY_PATTERN.test(key)) {
    throw new Error(`Invalid platform config env key: ${key}`);
  }
  if (DENIED_ENV_KEYS.has(key)) {
    throw new Error(`Platform config cannot override core runtime env key: ${key}`);
  }
  const normalized = asScalarEnvValue(value);
  if (normalized === undefined) {
    throw new Error(`Platform config env "${key}" must be string, number, or boolean`);
  }
  target[key] = normalized;
}

function flattenStringMap(value: unknown): string | undefined {
  if (!isObject(value)) return undefined;
  const normalized: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    const envValue = asScalarEnvValue(raw);
    if (envValue !== undefined) normalized[key] = envValue;
  }
  return Object.keys(normalized).length > 0 ? JSON.stringify(normalized) : undefined;
}

function flattenDocumentConfig(config: JsonObject, target: Record<string, string>): void {
  const document = config.document;
  if (!isObject(document)) return;

  const prompts = document.prompts;
  if (isObject(prompts)) {
    const ocr = asString(prompts.ocr);
    const openai = asString(prompts.openai);
    const gemini = asString(prompts.gemini);
    if (ocr) target.GENCOW_DOCUMENT_OCR_PROMPT = ocr;
    if (openai) target.GENCOW_DOCUMENT_OPENAI_PROMPT = openai;
    if (gemini) target.GENCOW_DOCUMENT_GEMINI_PROMPT = gemini;
  }

  const openai = document.openai;
  if (isObject(openai)) {
    const model = asString(openai.model);
    if (model) target.GENCOW_DOCUMENT_OPENAI_MODEL = model;
    if (openai.maxOutputTokens !== undefined) {
      setEnv(target, "GENCOW_DOCUMENT_OPENAI_MAX_OUTPUT_TOKENS", openai.maxOutputTokens);
    }
  }

  const gemini = document.gemini;
  if (isObject(gemini)) {
    const model = asString(gemini.model);
    if (model) target.GENCOW_DOCUMENT_GEMINI_MODEL = model;
  }

  const ocr = document.ocr;
  if (isObject(ocr)) {
    const model = asString(ocr.model);
    if (model) target.GENCOW_DOCUMENT_OCR_MODEL = model;
  }

  const safeAuto = document.safeAuto;
  if (isObject(safeAuto) && safeAuto.maxPaidFallbackCredits !== undefined) {
    setEnv(target, "GENCOW_DOCUMENT_AUTO_PAID_FALLBACK_MAX_CREDITS", safeAuto.maxPaidFallbackCredits);
  }

  const providers = document.providers;
  if (!isObject(providers)) return;

  if (Array.isArray(providers.forceOcrOrder)) {
    target.GENCOW_DOCUMENT_FORCE_OCR_PROVIDERS = providers.forceOcrOrder
      .map((value) => String(value).trim())
      .filter(Boolean)
      .join(",");
  }

  const kordoc = providers.kordoc;
  if (isObject(kordoc)) {
    const url = asString(kordoc.url);
    const token = asString(kordoc.token);
    if (url) target.GENCOW_DOCUMENT_KORDOC_URL = url;
    if (token) target.GENCOW_DOCUMENT_KORDOC_TOKEN = token;
    if (kordoc.timeoutMs !== undefined) {
      setEnv(target, "GENCOW_DOCUMENT_KORDOC_TIMEOUT_MS", kordoc.timeoutMs);
    }
  }

  const customVlm = providers.customVlm;
  if (!isObject(customVlm)) return;

  const url = asString(customVlm.url);
  const token = asString(customVlm.token);
  const model = asString(customVlm.model);
  const headers = flattenStringMap(customVlm.headers);
  if (url) target.GENCOW_DOCUMENT_CUSTOM_VLM_URL = url;
  if (token) target.GENCOW_DOCUMENT_CUSTOM_VLM_TOKEN = token;
  if (model) target.GENCOW_DOCUMENT_CUSTOM_VLM_MODEL = model;
  if (headers) target.GENCOW_DOCUMENT_CUSTOM_VLM_HEADERS_JSON = headers;
  if (customVlm.timeoutMs !== undefined) {
    setEnv(target, "GENCOW_DOCUMENT_CUSTOM_VLM_TIMEOUT_MS", customVlm.timeoutMs);
  }
}

function flattenTemplateConfig(config: JsonObject, target: Record<string, string>): void {
  const template = config.template;
  if (!isObject(template)) return;

  const preview = template.preview;
  if (isObject(preview)) {
    const adminEmail = asString(preview.adminEmail);
    const adminUserId = asString(preview.adminUserId);
    if (adminEmail) target.GENCOW_TEMPLATE_PREVIEW_ADMIN_EMAIL = adminEmail;
    if (adminUserId) target.GENCOW_TEMPLATE_PREVIEW_ADMIN_USER_ID = adminUserId;
  }

  const limits = template.limits;
  if (isObject(limits)) {
    if (limits.maxSourceBytes !== undefined) {
      setEnv(target, "GENCOW_TEMPLATE_MAX_SOURCE_BYTES", limits.maxSourceBytes);
    }
    if (limits.maxStaticBytes !== undefined) {
      setEnv(target, "GENCOW_TEMPLATE_MAX_STATIC_BYTES", limits.maxStaticBytes);
    }
  }

  const screenshots = template.screenshots;
  if (isObject(screenshots)) {
    const captureBaseUrl = asString(screenshots.captureBaseUrl);
    const captureToken = asString(screenshots.captureToken);
    if (captureBaseUrl) target.GENCOW_TEMPLATE_CAPTURE_BASE_URL = captureBaseUrl;
    if (captureToken) target.GENCOW_TEMPLATE_PREVIEW_CAPTURE_TOKEN = captureToken;
    if (screenshots.timeoutMs !== undefined) {
      setEnv(target, "GENCOW_TEMPLATE_SCREENSHOT_TIMEOUT_MS", screenshots.timeoutMs);
    }
    if (screenshots.maxConcurrency !== undefined) {
      setEnv(target, "GENCOW_TEMPLATE_SCREENSHOT_MAX_CONCURRENCY", screenshots.maxConcurrency);
    }
    if (screenshots.workerTimeoutMs !== undefined) {
      setEnv(target, "GENCOW_TEMPLATE_SCREENSHOT_WORKER_TIMEOUT_MS", screenshots.workerTimeoutMs);
    }
    if (screenshots.nice !== undefined) {
      setEnv(target, "GENCOW_TEMPLATE_SCREENSHOT_NICE", screenshots.nice);
    }
  }

  const storage = template.storage;
  if (isObject(storage)) {
    const dir = asString(storage.dir);
    if (dir) target.GENCOW_TEMPLATE_STORAGE_DIR = dir;
  }

  const checkout = template.checkout;
  if (isObject(checkout)) {
    const successUrl = asString(checkout.successUrl);
    const cancelUrl = asString(checkout.cancelUrl);
    if (successUrl) target.GENCOW_TEMPLATE_CHECKOUT_SUCCESS_URL = successUrl;
    if (cancelUrl) target.GENCOW_TEMPLATE_CHECKOUT_CANCEL_URL = cancelUrl;
  }
}

export function flattenPlatformRuntimeConfig(config: unknown): Record<string, string> {
  if (!isObject(config)) {
    throw new Error("Platform config JSON must be an object");
  }

  const env: Record<string, string> = {};
  if (isObject(config.env)) {
    for (const [key, value] of Object.entries(config.env)) {
      setEnv(env, key, value);
    }
  }
  flattenDocumentConfig(config, env);
  flattenTemplateConfig(config, env);
  return env;
}

function resolvePlatformConfigPath(params: {
  env: NodeJS.ProcessEnv;
  cwd: string;
  existsSyncImpl: typeof existsSync;
}): { path: string | null; explicit: boolean } {
  const explicit = params.env.GENCOW_PLATFORM_CONFIG_FILE?.trim();
  if (explicit) return { path: resolve(params.cwd, explicit), explicit: true };

  const localPath = resolve(params.cwd, LOCAL_PLATFORM_CONFIG_FILE);
  if (params.existsSyncImpl(localPath)) return { path: localPath, explicit: false };
  if (params.existsSyncImpl(DEFAULT_PLATFORM_CONFIG_PATH)) {
    return { path: DEFAULT_PLATFORM_CONFIG_PATH, explicit: false };
  }
  return { path: null, explicit: false };
}

export function loadPlatformRuntimeConfigFile(
  options: {
    env?: NodeJS.ProcessEnv;
    cwd?: string;
    logger?: LoggerLike;
    existsSyncImpl?: typeof existsSync;
    readFileSyncImpl?: typeof readFileSync;
  } = {},
): Record<string, string> {
  const env = options.env ?? process.env;
  if (env.IS_PLATFORM !== "true") return {};

  const cwd = options.cwd ?? process.cwd();
  const existsSyncImpl = options.existsSyncImpl ?? existsSync;
  const readFileSyncImpl = options.readFileSyncImpl ?? readFileSync;
  const resolved = resolvePlatformConfigPath({ env, cwd, existsSyncImpl });

  if (!resolved.path) return {};
  if (!existsSyncImpl(resolved.path)) {
    if (resolved.explicit) throw new Error(`Platform config file not found: ${resolved.path}`);
    return {};
  }

  const config = JSON.parse(readFileSyncImpl(resolved.path, "utf-8")) as unknown;
  const flattened = flattenPlatformRuntimeConfig(config);
  for (const [key, value] of Object.entries(flattened)) {
    env[key] = value;
  }
  options.logger?.log(
    `[platform-config] loaded ${Object.keys(flattened).length} setting(s) from ${resolved.path}`,
  );
  return flattened;
}
